import { useCallback, useEffect } from 'react';
import { css } from '@emotion/react';
import { CallDirection, Sentiment } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { CallIntelligenceUpdate } from '@weave/schema-gen-ts/dist/shared/call-intelligence/notification/message.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { CallStatus } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { CallRecordTagNotification } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import {
  VoicemailMessageAlertEvent,
  VoicemailTagsAlertEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import dayjs from 'dayjs';
import { PaymentOrigin } from '@frontend/api-invoices';
import { PersonHelpers } from '@frontend/api-person';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { IntelCard } from '@frontend/call-intel-insights';
import { ActionsUI } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { getFullName } from '@frontend/name';
import { usePanelStore } from '@frontend/panel-engine';
import { usePaymentTypeMap } from '@frontend/payments-hooks';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { openSchedulePulse, SchedulePulseTab } from '@frontend/schedule';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { dispatchTopBarTraySelectionEvent, useAppFlagStore } from '@frontend/shared';
import { encodeBase64, sentenceCase } from '@frontend/string';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { ThreadSendingAreaStores } from '@frontend/thread-sending-area';
import { ScheduleRequestNotification, ScheduleWaitlistNotification } from '@frontend/types';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Avatar, Chip, InlineNotification, Text, useAlert } from '@frontend/design-system';
import { useNotificationContext } from '../notification-provider';
import { getTitleFromPayload } from '../notification-utils';
import { getThreadAiNotificationClassification } from '../utils/thread-ai-notification-classification';
import { HistoricalNotification, HistoricalNotificationType, useVoicemailNotificationAction } from './helper';
//SMS
export const HistoricalSmsNotification = ({
  notification,
  isSMSThreadExpanded,
  lastElement,
  notificationHovered,
  ...props
}: HistoricalNotificationType<NotificationType.SMS> & {
  isSMSThreadExpanded?: boolean;
  lastElement?: boolean;
  notificationHovered?: boolean;
}) => {
  const { onClick } = ActionsUI.actions.useMessageAction();
  const { getThreadTypingUsers } = ThreadSendingAreaStores.useTypingIndicatorStore();
  const notificationTrayContext = useNotificationContext();
  const isNotificationTray = usePanelStore((state) => state.trayIds.includes('notifications'));
  const { getFeatureFlag } = useAppFlagStore();
  const locationId = notification.alert.payload?.locationId || notification.locationId;
  const departmentsIsDeprecated = getFeatureFlag({
    locationIds: [locationId],
    flagName: 'departments-deprecated',
  }).every((val) => !!val);
  const typingUsers = getThreadTypingUsers({
    locationId: notification.alert.payload?.locationId || '',
    threadId: notification.alert.payload?.threadIds?.[0] || '',
  });
  const notificationAction = () => {
    notificationTrayContext.markThreadNotificationsAsRead(
      notification.alert.payload?.threadIds?.[0] || '',
      notification.locationId
    );
    notificationTrayContext.syncReadStatusAmongUsers({ notificationType: NotificationType.SMS, notification });
    if (isNotificationTray) {
      props.closeModal();
    }
    onClick({
      threadId: notification.alert.payload?.threadIds?.[0],
      threadGroupId: notification.alert.payload?.locationId,
      groupIds: notification.alert.payload?.locationId ? [notification.alert.payload?.locationId] : undefined,
      personId: notification.alert.payload?.PersonID,
      personPhone: notification.alert.payload?.phoneMobile || notification.alert.payload?.patientNumber,
      departmentId: departmentsIsDeprecated ? undefined : notification.alert.payload?.departmentId,
      locationPhone: departmentsIsDeprecated ? notification.alert.payload?.weaveNumber : undefined,
    });
  };

  const name =
    getTitleFromPayload({
      number: notification?.alert?.payload?.phoneMobile || '',
      firstName: notification?.alert?.payload?.firstName || '',
      lastName: notification?.alert?.payload?.lastName || '',
    }) || '';

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-sms'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={notification.alert?.payload?.body || ''}
      icon={<Avatar name={name} size='xs' css={{ zIndex: 1 }} />}
      title={name}
      titlePostfix={
        typingUsers?.[0]?.firstName ? (
          <div
            css={{
              fontSize: theme.spacing(1.5),
              color: theme.colors.neutral70,
              marginBottom: '2px',
            }}
          >
            {typingUsers[0].firstName} is responding...
          </div>
        ) : (
          <></>
        )
      }
      isSMSThreadExpanded={isSMSThreadExpanded}
      wrapperStyles={
        lastElement
          ? { paddingBottom: theme.spacing(6), borderBottom: `1px solid ${theme.colors.neutral20}` }
          : { padding: `${theme.spacing(1.5)} ${theme.spacing(2)}` }
      }
      notificationHovered={notificationHovered}
    />
  );
};

//CALL TAG
export const HistoricalCallRecordTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.CALL_RECORD_TAG>) => {
  const { callerNumber, contactName, direction, startedAt, status, tagsPayload, callRecordId, locationId } =
    notification.alert.payload as CallRecordTagNotification;
  const notificationTrayContext = useNotificationContext();

  const { open: openCallInsightPanel, isOpen } = useCallInsightController({
    source: 'callRecordTagNotification',
  });

  const notificationAction = () => {
    if (callRecordId && locationId) {
      openCallInsightPanel({
        callRecordId,
        locationId,
        includeCallIntelligence: true,
      });
    }
  };

  useEffect(() => {
    if (isOpen && !notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
  }, [isOpen, notification.hasRead, notification.id, notificationTrayContext]);

  return (
    <HistoricalNotification
      trackingId='call-record-tag-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      title={contactName || formatPhoneNumber(callerNumber) || 'Unknown'}
      subtitle={
        <div
          css={css`
            display: flex;
            gap: ${theme.spacing(0.5)};
            align-items: center;
          `}
        >
          {String(direction) === CallDirection.DIRECTION_OUTBOUND && <Icon name='phone-outgoing' size={14} />}
          {String(direction) === CallDirection.DIRECTION_INBOUND && status !== CallStatus.CALL_STATUS_MISSED && (
            <Icon name='phone-incoming' size={14} />
          )}
          {String(direction) === CallDirection.DIRECTION_INBOUND && status === CallStatus.CALL_STATUS_MISSED && (
            <Icon name='phone-missed' size={14} />
          )}
          <Text size='small'>{dayjs(startedAt).format('MMM DD YYYY, hh:mm A')}</Text>
        </div>
      }
      titlePrefix={
        notification.alert.payload && (
          <Chip.Tag
            color={tagsPayload?.[0].color}
            css={{
              width: 'min-content',
              justifyContent: 'start',
              marginBottom: theme.spacing(1),
            }}
          >
            {tagsPayload?.[0].name}
          </Chip.Tag>
        )
      }
      {...props}
    />
  );
};

//SMS TAG
export const HistoricalSMSTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.SMS_TAG_V2>) => {
  const { t } = useTranslation('notifications');
  const { navigateToThread } = useInboxNavigate();
  const notificationTrayContext = useNotificationContext();
  const notificationAction = () => {
    if (!notification.hasRead) notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    if (!notification.alert.payload) return;
    navigateToThread({
      threadId: notification.alert.payload.threadId,
      groupId: notification.alert.payload.groupId,
      personId: notification.alert.payload.personId,
      personPhone: notification.alert.payload.personPhone,
      isNew: false,
      threadItemId: notification.alert.payload.smsId,
      threadItemCreatedAt: notification.alert.payload.smsCreatedAt,
    });
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='sms-tag-grouped-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={
        notification.alert.payload?.smsBody ||
        t('{{count}} attachments', { count: notification.alert.payload?.numMedia ?? 0 })
      }
      titlePrefix={
        notification.alert.payload && (
          <Chip.Tag
            color={notification.alert.payload?.tagColor}
            css={{
              width: 'min-content',
              justifyContent: 'start',
              marginBottom: theme.spacing(1),
            }}
          >
            {notification.alert.payload?.tagName}
          </Chip.Tag>
        )
      }
      title={
        PersonHelpers.getFullName({
          FirstName: notification.alert.payload?.personFirstName,
          LastName: notification.alert.payload?.personLastName,
          PreferredName: notification.alert.payload?.personPreferredName,
        }) || formatPhoneNumber(notification.alert.payload?.personPhone || '')
      }
      {...props}
    />
  );
};

//Inbox ROBOCALL TAG
export const HistoricalInboxRobocallTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.ROBOCALL_TAG>) => {
  const { onClick } = ActionsUI.actions.useMessageAction();
  const notificationTrayContext = useNotificationContext();

  const payload = notification.alert.payload;

  const notificationAction = () => {
    if (!notification.hasRead) notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    if (!payload) return;
    onClick({
      groupIds: [payload.locationId],
      personPhone: payload.personPhone,
      locationPhone: payload.weaveNumber,
    });
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='inbox-robocall-tag-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      title={
        PersonHelpers.getFullName({
          FirstName: payload?.personFirstName,
          LastName: payload?.personLastName,
          PreferredName: payload?.personPreferredName,
        }) || formatPhoneNumber(payload?.personPhone || '')
      }
      titlePrefix={
        payload && (
          <Chip.Tag
            color={payload.tagColor}
            css={{
              width: 'min-content',
              justifyContent: 'start',
              marginBottom: theme.spacing(1),
            }}
          >
            {payload.tagName}
          </Chip.Tag>
        )
      }
      subtitle={
        <div
          css={css`
            display: flex;
            gap: ${theme.spacing(0.5)};
            align-items: center;
          `}
        >
          <Icon name='phone-outgoing' size={14} />
          <Text size='small'>{dayjs(payload?.startedAt).format('MMM DD YYYY, hh:mm A')}</Text>
        </div>
      }
      {...props}
    />
  );
};

// THREAD AI STATE
export const HistoricalThreadAIStateNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.AI_RECEPTIONIST_STATE>) => {
  const { t } = useTranslation('notifications');
  const { onClick } = ActionsUI.actions.useMessageAction();
  const notificationTrayContext = useNotificationContext();
  const { getScopeName } = useAppScopeStore();

  const payload = notification.alert.payload?.threadAiActivity;
  const person = payload?.person;
  const preferredName = person?.preferredName || '';
  const firstName = person?.firstName || '';
  const lastName = person?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const displayName = preferredName || fullName || formatPhoneNumber(payload?.personPhoneNumber || '');
  const notificationClassification = payload ? getThreadAiNotificationClassification(payload) : null;
  const hasRelatedTask = notificationClassification?.category === 'needs-attention' && !!payload?.relatedTaskId;
  const location = getScopeName(payload?.locationId || notification.locationId || '');

  const notificationAction = useCallback(() => {
    if (!notification.hasRead) notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    if (!payload) return;
    onClick({
      groupIds: [payload.locationId ?? ''],
      personPhone: payload.personPhoneNumber,
      locationPhone: payload.locationPhoneNumber,
    });
  }, [notification.hasRead, notification.id, notificationTrayContext, onClick, payload]);

  return (
    <div
      css={css`
        .content-column {
          row-gap: ${theme.spacing(1)};
        }
      `}
    >
      <HistoricalNotification
        trackingId='ai-receptionist-state-notification-center-item'
        createdAt={notification.createdAt}
        hasRead={notification.hasRead}
        notificationAction={notificationAction}
        title={displayName}
        icon={<Avatar name={displayName} size='xs' css={{ zIndex: 1 }} />}
        children={location ? <Chip.Location>{location}</Chip.Location> : undefined}
        subtitle={
          notificationClassification ? (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(0.75),
                alignItems: 'flex-start',
                marginTop: theme.spacing(0.5),
              }}
            >
              <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
                <span
                  css={
                    notificationClassification.customIconColor
                      ? {
                          color: notificationClassification.customIconColor,
                        }
                      : undefined
                  }
                >
                  <Icon
                    name={notificationClassification.iconName}
                    size={16}
                    color={notificationClassification.iconColor}
                    css={
                      notificationClassification.category === 'needs-attention'
                        ? {
                            borderRadius: theme.borderRadius.small,
                            background: `linear-gradient(100deg, ${theme.colors.warning10} 0%, ${theme.colors.warning5} 100%)`,
                            svg: {
                              color: theme.colors.warning80,
                            },
                          }
                        : undefined
                    }
                  />
                </span>
                <Text size='medium'>{t(notificationClassification.label)}</Text>
              </div>
              {hasRelatedTask && (
                <div
                  css={{
                    display: 'flex',
                    gap: theme.spacing(0.5),
                    alignItems: 'center',
                    color: theme.colors.tangerine50,
                  }}
                >
                  <Icon name='task-created-ai' size={14} />
                  <Text size='medium' css={{ color: theme.colors.tangerine50 }}>
                    {t('Task Created!')}
                  </Text>
                </div>
              )}
            </div>
          ) : (
            notification.alert.body || ''
          )
        }
        {...props}
      />
    </div>
  );
};

//PAYMENTS
export const HistoricalPaymentsNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.PAYMENTS>) => {
  const navigate = useNavigate();
  const { t } = useTranslation('notifications');
  const notificationTrayContext = useNotificationContext();
  const { paymentTypeMap } = usePaymentTypeMap();
  const notificationAction = () => {
    const invoiceId = notification.alert.payload?.invoiceId;
    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    invoiceId
      ? navigate({ to: '/payments/invoices/$id', params: { id: invoiceId } })
      : navigate({ to: '/payments/invoices' });
    props.closeModal();
  };
  const paymentMode =
    paymentTypeMap[notification.alert.payload?.origin as PaymentOrigin] ?? notification.alert.payload?.origin;
  const subTitle = sentenceCase(t('{{paymentMode}} payment made', { paymentMode }));

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-payments'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={subTitle}
      title={notification.alert.payload?.patientName || ''}
      {...props}
    />
  );
};

//FORMS
export const HistoricalFormsNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.FORMS>) => {
  const navigate = useNavigate();
  const notificationTrayContext = useNotificationContext();
  const notificationAction = () => {
    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    navigate({
      to: `/forms/submissions/inbox/all/${notification.alert.payload?.submissionId}?locationId=${notification.locationId}`,
    });
    notificationTrayContext.syncReadStatusAmongUsers({ notificationType: NotificationType.FORMS, notification });
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-forms'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={notification.alert.body || ''}
      title={notification.alert.title || ''}
      {...props}
    />
  );
};

//REVIEWS
export const HistoricalReviewsNotification = ({
  notification,
  ...props
}:
  | HistoricalNotificationType<NotificationType.REVIEWS>
  | HistoricalNotificationType<NotificationType.REVIEW_COMMENT>) => {
  const navigate = useNavigate();
  const notificationTrayContext = useNotificationContext();
  const alert = useAlert();
  const { t } = useTranslation('notifications');

  const siteName = notification.alert?.payload?.siteName || '';
  const sanitizedSiteName = ['google', 'facebook', 'private'].includes(siteName) ? siteName : '';
  const notificationAction = () => {
    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    if (sanitizedSiteName) {
      navigate({
        to: `/reviews/${sanitizedSiteName}/${encodeBase64(
          notification.alert?.payload?.reviewId || ''
        )}?type=${sanitizedSiteName}`,
      });
      props.closeModal();
    } else {
      alert.error(t('Error navigating to reviews'));
    }

    notificationTrayContext.syncReadStatusAmongUsers({ notificationType: NotificationType.REVIEWS, notification });
  };

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-reviews'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={notification.alert.payload?.review || ''}
      title={notification.alert.title || ''}
      {...props}
    />
  );
};

//FAX
export const HistoricalFaxNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.FAX>) => {
  const navigate = useNavigate();
  const notificationTrayContext = useNotificationContext();
  const { t } = useTranslation('fax');
  const alert = useAlert();
  const notificationAction = () => {
    if (!notification?.alert?.payload?.faxId) {
      alert.error(t('Error opening fax'));
      return;
    }
    navigate({
      to: '/fax/$filter/$faxId',
      params: { filter: 'inbox', faxId: notification.alert.payload.faxId },
      search: { isNotification: true },
    });
    notificationTrayContext.syncReadStatusAmongUsers({ notificationType: NotificationType.FAX, notification });
    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-fax'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={notification.alert.body || ''}
      title={notification.alert.title || ''}
      {...props}
    />
  );
};

//FAX TAG
export const HistoricalFaxTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.FAX_TAG>) => {
  const navigate = useNavigate();
  const notificationTrayContext = useNotificationContext();
  const { t } = useTranslation('fax');
  const alert = useAlert();
  const notificationAction = () => {
    if (!notification?.alert?.payload?.faxId) {
      alert.error(t('Error opening fax'));
      return;
    }
    navigate({
      to: '/fax/$filter/$faxId',
      params: { filter: 'inbox', faxId: notification.alert.payload.faxId },
      search: { isNotification: true },
    });

    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-fax-tag'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={notification.alert.payload?.title || ''}
      title={
        PersonHelpers.getFullName({
          FirstName: notification.alert.data?.firstName,
          LastName: notification.alert.data?.lastName,
        }) || formatPhoneNumber(notification.alert.data?.number || '')
      }
      titlePrefix={
        notification.alert.data && (
          <Chip.Tag
            color={notification.alert.data?.tagColor}
            css={{
              width: 'min-content',
              justifyContent: 'start',
            }}
          >
            {notification.alert.data?.tagName}
          </Chip.Tag>
        )
      }
      {...props}
    ></HistoricalNotification>
  );
};

//VOICEMAIL TAG
export const HistoricalVoicemailTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.VOICEMAIL_TAG>) => {
  const { t } = useTranslation('notifications');

  const {
    voicemailMessageId,
    callerNumber,
    personContact,
    voicemailCreatedAt,
    mailboxName,
    userHasAccess,
    locationId,
    tagsNotificationPayload,
  } = notification.alert.payload as VoicemailTagsAlertEvent;

  const { notificationAction } = useVoicemailNotificationAction({
    notification,
    voicemailId: voicemailMessageId || '',
    locationId: locationId || '',
    voicemailCreatedAt,
    ...props,
  });

  return (
    <HistoricalNotification
      trackingId='voicemail-tag-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      title={personContact || formatPhoneNumber(callerNumber) || 'Unknown'}
      titlePrefix={
        <Chip.Tag
          color={tagsNotificationPayload?.[0].color}
          css={{
            width: 'min-content',
            justifyContent: 'start',
          }}
        >
          {tagsNotificationPayload?.[0]?.name}
        </Chip.Tag>
      }
      {...props}
    >
      {notification.alert.payload && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          {userHasAccess && (
            <>
              <Text size='medium'>{dayjs(voicemailCreatedAt).format('MMM DD YYYY, hh:mm A')}</Text>

              <div
                css={css`
                  display: flex;
                  gap: ${theme.spacing(0.5)};
                  align-items: center;
                `}
              >
                <Icon name='voicemail-small' />
                <Text
                  size='medium'
                  css={css`
                    margin: 0;
                  `}
                >
                  {mailboxName}
                </Text>
              </div>
            </>
          )}
          {!userHasAccess && (
            <InlineNotification status='error' size='small' message={t('This voicemail is restricted.')} />
          )}
        </div>
      )}
    </HistoricalNotification>
  );
};

//VOICEMAIL MESSAGE
export const HistoricalVoicemailMessageNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.VOICEMAIL_MESSAGE>) => {
  const { voicemailMessageId, mailboxName, locationId, createdAt, personContact, callerNumber, transcriptionSnippet } =
    notification.alert.payload as VoicemailMessageAlertEvent;

  const { notificationAction } = useVoicemailNotificationAction({
    notification,
    voicemailId: voicemailMessageId || '',
    locationId: locationId || '',
    voicemailCreatedAt: createdAt,
    ...props,
  });

  return (
    <HistoricalNotification
      trackingId='voicemail-message-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      title={personContact || formatPhoneNumber(callerNumber) || 'Unknown'}
      {...props}
    >
      {notification.alert.payload && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          {!!transcriptionSnippet && (
            <Text
              weight='regular'
              size='medium'
              css={css`
                margin: 0;
              `}
            >
              <i>
                <q>{transcriptionSnippet}</q>
              </i>
            </Text>
          )}
          <Text size='medium'>{dayjs(createdAt).format('MMM DD YYYY, hh:mm A')}</Text>
          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(0.5)};
              align-items: center;
            `}
          >
            <Icon name='voicemail-small' />
            <Text
              size='medium'
              css={css`
                margin: 0;
              `}
            >
              {mailboxName}
            </Text>
          </div>
        </div>
      )}
    </HistoricalNotification>
  );
};

export const HistoricalCallIntelUnhappyCallNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL>) => {
  const notificationTrayContext = useNotificationContext();

  const { callId, categories, locationId, person, phoneNumber, startTime } = notification.alert
    .payload as CallIntelligenceUpdate;

  const { open: openCallInsightPanel } = useCallInsightController({
    source: 'unhappyCallNotification',
  });

  const notificationAction = () => {
    if (callId && locationId) {
      openCallInsightPanel({
        channelId: callId,
        locationId: locationId,
        includeCallIntelligence: true,
      });
    }

    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
  };

  return (
    <HistoricalNotification
      trackingId='unhappy-call-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={<Text size='medium'>{dayjs(startTime).format('MMM DD YYYY, hh:mm A')}</Text>}
      title={
        getFullName({
          firstName: person?.firstName,
          lastName: person?.lastName,
          preferredName: person?.preferredName,
        }) || formatPhoneNumber(phoneNumber?.nationalNumber?.toString() ?? '')
      }
      titlePrefix={
        <div
          css={css`
            margin-bottom: ${theme.spacing(1)};
          `}
        >
          <IntelCard.Attributes>
            <IntelCard.Sentiment sentiment={Sentiment.SENTIMENT_NEGATIVE} />
            <IntelCard.Category category={categories?.[0]} />
          </IntelCard.Attributes>
        </div>
      }
      {...props}
    />
  );
};

export const FallbackNotification = ({ notification, ...props }: HistoricalNotificationType<any>) => {
  return (
    <HistoricalNotification
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      subtitle={notification.alert.body || ''}
      title={notification.alert.title || ''}
      {...props}
    />
  );
};

export const HistoricalTaskNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.TASK>) => {
  const { onViewNotification } = useTaskCenterStore(['onViewNotification']);
  const notificationTrayContext = useNotificationContext();
  const notificationAction = () => {
    onViewNotification(notification.alert.payload);
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
  };

  return (
    <HistoricalNotification
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      title={notification.alert.payload?.title ?? ''}
      subtitle={notification.alert.payload?.body}
      notificationAction={notificationAction}
      {...props}
    />
  );
};

export const HistoricalScheduleRequestNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.SCHEDULE_REQUEST>) => {
  const { t } = useTranslation('notifications');
  const { getCustomizationFlag } = useScopedAppFlagStore();
  const isScheduleRequestNotificationFeatureEnabled = getCustomizationFlag('online-scheduling');

  const notificationTrayContext = useNotificationContext();

  const { payload } = notification.alert as ScheduleRequestNotification;

  const notificationAction = () => {
    notificationTrayContext.syncReadStatusAmongUsers({
      notificationType: NotificationType.SCHEDULE_REQUEST,
      notification,
    });
    openSchedulePulse(SchedulePulseTab.AppointmentRequests);
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
  };

  const name = getFullName(payload?.schedulee || {});

  const subtitle = name && notification.alert.subtitle ? `from ${name} ${notification.alert.subtitle}` : '';

  if (!isScheduleRequestNotificationFeatureEnabled) {
    return;
  }

  return (
    <HistoricalNotification
      trackingId='notification-center:notification-item:new-schedule-request'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={subtitle}
      title={t('New Schedule Request')}
      {...props}
    />
  );
};

export const HistoricalScheduleWaitlistNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.SCHEDULE_WAITLIST_UPDATE>) => {
  const { t } = useTranslation('notifications');
  const { getCustomizationFlag } = useScopedAppFlagStore();
  const isScheduleQuickFillEnabled = getCustomizationFlag('quickfill');

  const notificationTrayContext = useNotificationContext();

  const { payload } = notification.alert as ScheduleWaitlistNotification;

  const notificationAction = () => {
    dispatchTopBarTraySelectionEvent('quickfill');
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
  };

  const name = payload.inviteeName ?? '';

  const subtitle = name && notification.alert.subtitle ? `from ${name} ${notification.alert.subtitle}` : '';

  if (!isScheduleQuickFillEnabled) {
    return;
  }

  return (
    <HistoricalNotification
      trackingId='notification-center:notification-item:schedule-waitlist'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      subtitle={subtitle}
      title={t('Waitlist')}
      {...props}
    />
  );
};
